import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Copy } from "lucide-react";

// Compact popup for purchasers to create a product code on the fly (from the
// New/Edit request screen) when a typed code isn't in the catalog. Two modes:
// "new" (blank) and "similar" (copy description/unit/price from an existing code).
export default function ProductQuickCreateDialog({ open, onOpenChange, mode = "new", initialCode = "", suggestions = [], onCreated }) {
  const [form, setForm] = useState({ code: "", name: "", unit: "adet", price: "" });
  const [baseCode, setBaseCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    const code = (initialCode || "").trim().toUpperCase();
    if (mode === "similar" && suggestions.length > 0) {
      const base = suggestions[0];
      setBaseCode(base.code);
      setForm({ code, name: base.name || "", unit: base.unit || "adet", price: base.price ?? "" });
    } else {
      setBaseCode("");
      setForm({ code, name: "", unit: "adet", price: "" });
    }
    setErr("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialCode]);

  const applyBase = (code) => {
    setBaseCode(code);
    const b = suggestions.find((s) => s.code === code);
    if (b) setForm((f) => ({ ...f, name: b.name || "", unit: b.unit || "adet", price: b.price ?? "" }));
  };

  const save = async () => {
    const code = form.code.trim().toUpperCase();
    const name = form.name.trim();
    if (!code) { setErr("Kod girin"); return; }
    if (!name) { setErr("Açıklama girin"); return; }
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.post("/products", {
        code,
        name,
        unit: form.unit.trim() || "adet",
        price: form.price === "" || form.price == null ? null : Number(form.price),
      });
      toast.success(`Ürün eklendi: ${data.code}`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Ürün eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quick-product-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === "similar" ? <Copy className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
            {mode === "similar" ? "Benzer Koddan Ürün Oluştur" : "Yeni Ürün Oluştur"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {mode === "similar"
              ? "Benzer bir üründen açıklama/birim/fiyat kopyalanır; kodu düzenleyip kaydedin."
              : "Yeni ürün kodu ve açıklaması girin. Liste fiyatı opsiyoneldir."}
          </DialogDescription>
        </DialogHeader>

        {mode === "similar" && suggestions.length > 0 && (
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Kaynak (benzer ürün)</Label>
            <select
              data-testid="quick-product-base"
              value={baseCode}
              onChange={(e) => applyBase(e.target.value)}
              className="mt-1.5 w-full h-11 border border-slate-200 rounded-md px-2 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-slate-900"
            >
              {suggestions.map((s) => <option key={s.id} value={s.code}>{s.code} — {s.name}</option>)}
            </select>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Kod</Label>
            <Input
              data-testid="quick-product-code"
              className="mt-1.5 h-11"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Açıklama</Label>
            <Input
              data-testid="quick-product-name"
              className="mt-1.5 h-11"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Birim</Label>
              <Input
                data-testid="quick-product-unit"
                className="mt-1.5 h-11"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Liste Fiyat (ops.)</Label>
              <Input
                data-testid="quick-product-price"
                type="number"
                inputMode="decimal"
                step="any"
                min="0"
                placeholder="—"
                className="mt-1.5 h-11"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </div>
          </div>
          {err && <p data-testid="quick-product-error" className="text-xs text-rose-600">{err}</p>}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Vazgeç</Button>
          <Button data-testid="quick-product-save" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet ve Devam Et"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
